import { cva, type VariantProps } from 'class-variance-authority';
import type { ElementType, HTMLAttributes, ReactNode } from 'react';
import { cn } from '../lib/cn';

const heading = cva('font-display font-black tracking-tight text-text-primary', {
  variants: {
    size: {
      sm: 'text-lg',
      md: 'text-xl',
      lg: 'text-2xl',
      xl: 'text-3xl',
      display: 'text-4xl font-black',
    },
  },
  defaultVariants: { size: 'md' },
});

export interface HeadingProps
  extends HTMLAttributes<HTMLHeadingElement>, VariantProps<typeof heading> {
  /** Semantic level – chosen by document outline, independent of visual size. */
  level: 1 | 2 | 3 | 4;
  children: ReactNode;
}

export function Heading({ level, size, className, ...props }: HeadingProps) {
  const Tag = `h${level}` as const;
  return <Tag {...props} className={cn(heading({ size }), className)} />;
}

const text = cva('', {
  variants: {
    size: { xs: 'text-xs', sm: 'text-sm', md: 'text-md', lg: 'text-lg', xl: 'text-xl' },
    tone: {
      primary: 'text-text-primary',
      secondary: 'text-text-secondary',
      muted: 'text-text-muted',
      success: 'text-feedback-success',
      danger: 'text-feedback-danger',
      inherit: '',
    },
    weight: {
      regular: 'font-regular',
      medium: 'font-medium',
      semibold: 'font-semibold',
      bold: 'font-bold',
    },
  },
  defaultVariants: { size: 'md', tone: 'primary', weight: 'regular' },
});

export interface TextProps extends HTMLAttributes<HTMLElement>, VariantProps<typeof text> {
  as?: ElementType;
  children: ReactNode;
}

export function Text({ as: Tag = 'p', size, tone, weight, className, ...props }: TextProps) {
  return <Tag {...props} className={cn(text({ size, tone, weight }), className)} />;
}
